import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase/firebaseConfig";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    addDoc,
    updateDoc,
    serverTimestamp,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { ref as storageRef, uploadString, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase/firebaseConfig";
import { useBanner } from "../components/BannerContext";

function SchedulingRequest({ therapistId, onStatusChange }) {
    const { t } = useTranslation();
    const [request, setRequest] = useState(null);
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [note, setNote] = useState("");
    const { i18n } = useTranslation();
    const isRTL = i18n.dir() === "rtl";
    const formatGoogleDate = (date) => date.toISOString().replace(/[-:]|\.\d{3}/g, "");
    const { showBanner } = useBanner();
    //const isAthlete = user?.role === "athlete";

    const generateGoogleCalendarLink = (title, start, end, description) => {
        return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formatGoogleDate(start)}/${formatGoogleDate(end)}&details=${encodeURIComponent(description)}`;
    };

    const uploadICSAndGetURL = async (title, start, end, description) => {
        const toICSDate = (date) =>
            date.toISOString().replace(/[-:]|\.\d{3}/g, "").split(".")[0];

        const ics = `BEGIN:VCALENDAR
      VERSION:2.0
      BEGIN:VEVENT
      SUMMARY:${title}
      DESCRIPTION:${description}
      DTSTART:${toICSDate(start)}
      DTEND:${toICSDate(end)}
      END:VEVENT
      END:VCALENDAR`;

        const fileName = `calendar-events/${Date.now()}-${title}.ics`;
        const ref = storageRef(storage, fileName);

        await uploadString(ref, ics, "raw", { contentType: "text/calendar" });
        const url = await getDownloadURL(ref);
        return url;
    };

    const uid = auth.currentUser?.uid;

    useEffect(() => {
        const fetchRequest = async () => {
            if (!uid || !therapistId) return;

            const q = query(
                collection(db, "schedulingRequests"),
                where("athleteId", "==", uid),
                where("therapistId", "==", therapistId)
            );

            const snapshot = await getDocs(q);
            const latest = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => b.updatedAt?.seconds - a.updatedAt?.seconds)[0]; // pick latest

            if (latest) {
                const sessionTime = new Date(latest.requestedTime);
                const now = new Date();

                // ❗ Clear approved requests that are in the past
                if (latest.status === "approved" && sessionTime < now) {
                    setRequest(null);
                    if (onStatusChange) onStatusChange(null);
                } else {
                    setRequest(latest);
                    if (onStatusChange) onStatusChange(latest.status);
                }
            } else {
                setRequest(null);
                if (onStatusChange) onStatusChange(null);
            }
        };

        fetchRequest();
    }, [uid, therapistId]);

    const handleSubmit = async () => {
        const requestedTime = new Date(`${date}T${time}`);
        if (!date || !time || !requestedTime || isNaN(requestedTime)) {
            showBanner("Please enter a valid date and time.", "error");
            return;
        }

        if (request?.status === "rejected") {
            // Update existing rejected request
            const ref = doc(db, "schedulingRequests", request.id);
            await updateDoc(ref, {
                requestedTime: requestedTime.toISOString(),
                note,
                status: "pending",
                updatedAt: serverTimestamp(),
            });
        } else {
            // New request
            const newDoc = await addDoc(collection(db, "schedulingRequests"), {
                athleteId: uid,
                therapistId,
                requestedTime: requestedTime.toISOString(),
                note,
                status: "pending",
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            // Notify therapist
            await addDoc(collection(db, "users", therapistId, "notifications"), {
                type: "schedule_request",
                athleteId: uid,
                requestId: newDoc.id,
                timestamp: new Date(),
                read: false,
            });
        }

        showBanner("⏳ Request submitted to therapist.", "success");
        setRequest({
            ...request,
            status: "pending",
            requestedTime: requestedTime.toISOString(),
            note,
        });
    };

    if (request?.status === "pending") {
        return (
            <div className="bg-yellow-100 p-4 rounded">
                <p className="font-medium">⏳ {t("waiting_for_therapist_confirmation")}</p>
                <p>{new Date(request.requestedTime).toLocaleString()}</p>
                {request.note && <p className="text-sm text-gray-600">📝 {request.note}</p>}
            </div>
        );
    }

    /* if (request?.status === "approved") {
        return (
            <div className="bg-green-100 p-4 rounded">
                <p className="font-medium">✅ {t("session_approved")}</p>
                <p>{new Date(request.requestedTime).toLocaleString()}</p>
                {request.note && <p className="text-sm text-gray-600">📝 {request.note}</p>}
            </div>
        );
    } */

    return (
        <div
            className="bg-white p-4 rounded shadow-md space-y-3"
            dir={isRTL ? "rtl" : "ltr"}
            style={{ textAlign: isRTL ? "right" : "left" }}
        >

            {/* ✅ Show this block if session is approved */}
            {request?.status === "approved" && (
                <div className="bg-green-100 p-4 rounded space-y-2">
                    <p className="font-medium">✅ {t("session_approved")}</p>
                    <p>{new Date(request.requestedTime).toLocaleString()}</p>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        {/* Google Calendar */}
                        <button
                            className="underline text-blue-600 text-left"
                            onClick={() => {
                                const url = generateGoogleCalendarLink(
                                    "Therapy Session",
                                    new Date(request.requestedTime),
                                    new Date(new Date(request.requestedTime).getTime() + 60 * 60 * 1000),
                                    "Session scheduled via Athar app"
                                );
                                window.location.href = url;
                            }}
                        >
                            {t("add_to_google_calendar")}
                        </button>

                        {/* Apple Calendar */}
                        <button
                            className="underline text-blue-600 text-left"
                            onClick={() => {
                                const toICSDate = (date) =>
                                    date.toISOString().replace(/[-:]|\.\d{3}/g, "").split(".")[0];

                                const start = new Date(request.requestedTime);
                                const end = new Date(start.getTime() + 60 * 60 * 1000); // 1 hour

                                const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
SUMMARY:Therapy Session
DESCRIPTION:Session scheduled via Athar app
DTSTART:${toICSDate(start)}
DTEND:${toICSDate(end)}
END:VEVENT
END:VCALENDAR`;

                                const blob = new Blob([icsContent], { type: "text/calendar" });
                                const url = URL.createObjectURL(blob);

                                const a = document.createElement("a");
                                a.href = url;
                                a.download = "session.ics";
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                            }}
                        >
                            {t("add_to_apple_calendar")}
                        </button>


                    </div>

                </div>
            )}

            {request?.status === "rejected" && (
                <div className="bg-red-100 text-red-700 p-2 rounded">
                    ❌ {t("session_rejected")}. {request.reason || ""}
                </div>
            )}

            <label className="block">
                {t("select_date")}
                <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full p-2 border rounded"
                />
            </label>

            <label className="block">
                {t("select_time")}
                <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full p-2 border rounded"
                />
            </label>

            <label className="block">
                {t("optional_note")}
                <textarea
                    rows="2"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="w-full p-2 border rounded"
                />
            </label>

            <button
                className="bg-[#b22222] text-white px-4 py-2 rounded"
                onClick={handleSubmit}
            >
                {request?.status === "rejected" ? t("resubmit_request") : t("submit_request")}
            </button>
        </div>
    );
}

export default SchedulingRequest;
